// EditSignatureModal.tsx
import React, { useEffect, useState } from "react";
import ReactModal from "react-modal";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useUpdateSignMutation } from "../../../../Services/settingsAPIs/SignatureApi";
import { toast } from "react-toastify";
import { useSmtpMailConfigsQuery } from "../../../../Services/settingsAPIs/EmailConfiguration";
import { useUsersByTeamIdQuery } from "../../../../Services/ticket/users/UsersByTeamId";

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  signatureData: any;
  onSaveSuccess: () => void;
  selectedTeam: number;
  emailIMAPmailConfigID: any;
}

const EditSignatureModal: React.FC<Props> = ({
  isOpen,
  closeModal,
  signatureData,
  onSaveSuccess,
  selectedTeam,
  emailIMAPmailConfigID,
}) => {
  // Check if this signature behaves as default (for display purposes)
  const behavesAsDefault =
    signatureData?.is_default === true ||
    (signatureData?.is_default === false && signatureData?.user === null);

  // Show checkbox for both: is_default === true OR (is_default === false && user === null)
  const showDefaultCheckbox = behavesAsDefault;

  const [editorContent, setEditorContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const [updateSignature] = useUpdateSignMutation();

  const teamId = localStorage.getItem("teamId");
  // const { data: emailIMAPmailConfigData } = useSmtpMailConfigsQuery(teamId);
  // const emailIMAPmailConfigID = emailIMAPmailConfigData?.[0]?.id;

  const { data: usersData, isLoading: usersLoading } = useUsersByTeamIdQuery(
    selectedTeam,
    {
      skip: !selectedTeam,
    }
  );

  // Load signature data when modal opens
  useEffect(() => {
    if (isOpen && signatureData) {
      setEditorContent(signatureData.signature_html || "");
      setSelectedUserId(signatureData.user || null);
      // Set isDefault based on the actual is_default value from data
      setIsDefault(signatureData.is_default === true);
      formik.setValues({
        name: signatureData.name || "",
      });
    }
  }, [isOpen, signatureData]);

  const formik = useFormik({
    initialValues: {
      name: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Signature name is required"),
    }),
    onSubmit: async (values) => {
      if (!editorContent || editorContent.trim() === "<p><br></p>") {
        toast.error("Signature content is required");
        return;
      }

      setIsSaving(true);

      try {
        const payload = {
          id: signatureData.id,
          body: {
            name: values.name.trim(),
            signature_html: editorContent,
            smtp_configuration: emailIMAPmailConfigID,
            is_default: showDefaultCheckbox ? isDefault : false, // Use checkbox value for default signatures, false for individual
            user: showDefaultCheckbox && isDefault ? null : selectedUserId, // null if default checked, otherwise userId
            team_id: selectedTeam,
          },
        };

        console.log("Update Signature Payload:", payload);
        await updateSignature(payload).unwrap();
        toast.success("Signature updated successfully");

        onSaveSuccess();
        closeModal();
      } catch (error: any) {
        const errData = error?.data;

        if (errData?.name) {
          toast.error(errData.name[0]);
        } else if (errData?.signature_html) {
          toast.error(errData.signature_html[0]);
        } else if (errData?.smtp_configuration) {
          toast.error(errData.smtp_configuration[0]);
        } else if (errData?.user) {
          toast.error(errData.user[0]);
        } else if (errData?.message) {
          toast.error(errData.message);
        } else {
          toast.error("Failed to update signature, please try again.");
        }
      } finally {
        setIsSaving(false);
      }
    },
    enableReinitialize: false,
  });

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "color",
    "align",
    "script",
    "background",
    "code-block",
  ];

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }, { font: [] }],
      ["bold", "italic", "underline", "strike"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image", "blockquote", "code-block"],
      [{ color: [] }, { background: [] }, { align: [] }],
      ["clean"],
    ],
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={closeModal}
      contentLabel="Edit signature"
      ariaHideApp={false}
      style={{
        content: {
          top: "50%",
          left: "50%",
          right: "auto",
          bottom: "auto",
          marginRight: "-50%",
          transform: "translate(-50%, -50%)",
          maxWidth: "600px",
          width: "90%",
          maxHeight: "90vh",
          overflow: "auto",
          padding: "20px",
          borderRadius: "8px",
          border: "none",
        },
        overlay: {
          backgroundColor: "rgba(0,0,0,0.5)",
          zIndex: 1050,
        },
      }}
      shouldCloseOnOverlayClick={!isSaving}
    >
      <button
        onClick={closeModal}
        disabled={isSaving}
        style={{
          float: "right",
          fontSize: "18px",
          border: "none",
          background: "none",
          cursor: "pointer",
        }}
        aria-label="Close modal"
      >
        &times;
      </button>
      <h2>Edit signature</h2>

      <form onSubmit={formik.handleSubmit}>
        {/* Show Default Checkbox for default signatures */}
        {showDefaultCheckbox && (
          <div className="mb-3 mt-3">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="isDefaultCheckbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                disabled={isSaving}
              />
              <label className="form-check-label" htmlFor="isDefaultCheckbox">
                <i className="bi bi-star-fill text-warning me-2"></i>
                Set as Default Team Signature
              </label>
            </div>
            <small className="text-muted">
              {isDefault
                ? "Will be applied to all team members without individual signatures"
                : "Not set as default signature"}
            </small>
          </div>
        )}

        {/* Show info for individual signatures */}
        {!behavesAsDefault && (
          <div className="alert alert-info mt-3">
            <i className="bi bi-person-fill me-2"></i>
            <strong>Individual User Signature</strong>
            <p className="mb-0 mt-1 small">
              This signature overrides the default team signature for a specific
              user.
            </p>
          </div>
        )}

        {/* Show assigned user info for individual signatures */}
        {!behavesAsDefault && selectedUserId && (
          <div className="mb-3">
            <label className="form-label fw-semibold">
              <i className="bi bi-person me-2"></i>
              Assigned User
            </label>
            <div className="form-control bg-light">
              {usersData?.find((u: any) => u.id === selectedUserId)?.name ||
                usersData?.find((u: any) => u.id === selectedUserId)
                  ?.username ||
                `User ID: ${selectedUserId}`}
            </div>
            <small className="text-muted">
              User assignment cannot be changed after creation
            </small>
          </div>
        )}

        <label htmlFor="name" className="form-label mt-3">
          Signature Name <span className="text-danger">*</span>
        </label>
        <input
          id="name"
          name="name"
          className="form-control"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={isSaving}
          placeholder="e.g., Sales Team Signature, John's Signature"
        />
        {formik.touched.name && formik.errors.name && (
          <div className="text-danger">{formik.errors.name}</div>
        )}

        <label className="form-label mt-3">
          Signature Content <span className="text-danger">*</span>
        </label>
        <ReactQuill
          value={editorContent}
          onChange={setEditorContent}
          modules={modules}
          formats={formats}
          readOnly={isSaving}
          placeholder="Type your signature here..."
        />

        <div className="mt-4 text-end">
          <button
            type="button"
            className="btn btn-secondary me-2"
            onClick={closeModal}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSaving}>
            {isSaving ? "Updating..." : "Update signature"}
          </button>
        </div>
      </form>
    </ReactModal>
  );
};

export default EditSignatureModal;
